/*
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license infornbion.
 */

import { Directive, ElementRef, InjectionToken, Input } from '@angular/core';
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkFooterCell,
  CdkFooterCellDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
} from '@angular/cdk/table';

/**
 * Cell definition for the el-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
@Directive({
  selector: '[elCellDef]',
  providers: [{ provide: CdkCellDef, useExisting: ElCellDefDirective }],
})
export class ElCellDefDirective extends CdkCellDef {
}

/**
 * Header cell definition for the el-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[elHeaderCellDef]',
  providers: [{ provide: CdkHeaderCellDef, useExisting: ElHeaderCellDefDirective }],
})
export class ElHeaderCellDefDirective extends CdkHeaderCellDef {
}

/**
 * Footer cell definition for the el-table.
 * Captures the template of a column's footer cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[elFooterCellDef]',
  providers: [{ provide: CdkFooterCellDef, useExisting: ElFooterCellDefDirective }],
})
export class ElFooterCellDefDirective extends CdkFooterCellDef {
}

export const EL_SORT_HEADER_COLUMN_DEF = new InjectionToken('EL_SORT_HEADER_COLUMN_DEF');

/**
 * Column definition for the el-table.
 * Defines a set of cells available for a table column.
 */
@Directive({
  selector: '[elColumnDef]',
  providers: [
    { provide: CdkColumnDef, useExisting: ElColumnDefDirective },
    { provide: EL_SORT_HEADER_COLUMN_DEF, useExisting: ElColumnDefDirective },
  ],
})
export class ElColumnDefDirective extends CdkColumnDef {
  /** Unique name for this column. */
  @Input('elColumnDef') name: string;

  /** Whether this column should be sticky positioned at the start of the row */
  @Input() sticky: boolean;

  /** Whether this column should be sticky positioned on the end of the row */
  @Input() stickyEnd: boolean;
}

/** Header cell template container that adds the right classes and role. */
@Directive({
  selector: 'el-header-cell, th[elHeaderCell]',
  host: {
    'class': 'el-header-cell',
    'role': 'columnheader',
  },
})
export class ElHeaderCellDirective extends CdkHeaderCell {
  constructor(columnDef: ElColumnDefDirective,
    elementRef: ElementRef<HTMLElement>) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(`el-column-${columnDef.cssClassFriendlyName}`);
  }
}

/** Footer cell template container that adds the right classes and role. */
@Directive({
  selector: 'el-footer-cell, td[elFooterCell]',
  host: {
    'class': 'el-footer-cell',
    'role': 'gridcell',
  },
})
export class ElFooterCellDirective extends CdkFooterCell {
  constructor(columnDef: ElColumnDefDirective,
    elementRef: ElementRef) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(`el-column-${columnDef.cssClassFriendlyName}`);
  }
}

/** Cell template container that adds the right classes and role. */
@Directive({
  selector: 'el-cell, td[elCell]',
  host: {
    'class': 'el-cell',
    'role': 'gridcell',
  },
})
export class ElCellDirective extends CdkCell {
  constructor(columnDef: ElColumnDefDirective,
    elementRef: ElementRef<HTMLElement>) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(`el-column-${columnDef.cssClassFriendlyName}`);
  }
}
